import { useMenu } from '@/hooks/useMenu'
import { useMenuPreferences } from '@/hooks/useMenuPreferences'
import Head from 'next/head'
import { useRouter } from 'next/router'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  MdClose,
  MdExpandLess,
  MdExpandMore,
  MdHistory,
  MdMenuOpen,
  MdSearch
} from 'react-icons/md'

import { Dropdown } from './dropdown'
import { MenuItem } from './item'
import { MenuRail } from './rail'
import { Container, ScrollMenu } from './styles'
import {
  collectMenuLeaves,
  filterMenu,
  findActiveLeaf,
  IMenuLeaf
} from './utils'

// Tempo entre a última tecla e a filtragem: segura o rebuild da lista sem o campo parecer travado.
const SEARCH_DEBOUNCE = 120

const Menu: React.FC = () => {
  const { active, onChangeActive, config, open, setOpen } = useMenu()

  const {
    pinned,
    recent,
    isPinned,
    togglePin,
    reorderPinned,
    registerVisit,
    collapsed,
    setCollapsed,
    recentCollapsed,
    setRecentCollapsed
  } = useMenuPreferences()

  // Grupo expandido, identificado pela rota — o índice não serve porque a lista muda de tamanho
  // durante a busca.
  const [groupOpen, setGroupOpen] = useState<string | null>(null)

  const [searchInput, setSearchInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  const router = useRouter()

  const searchInputRef = useRef<HTMLInputElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  function useOutsideAlerter(ref) {
    useEffect(() => {
      function handleClickOutside(event) {
        if (ref.current && !ref.current.contains(event.target)) {
          if (!open) {
            setGroupOpen(null)
          }
        }
      }
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [ref, open])
  }

  // * O primeiro render usa o que está no navegador: a preferência do servidor chega depois e o
  // * menu não pode piscar de recolhido para aberto enquanto ela não vem.
  useEffect(() => {
    if (
      localStorage.getItem('@BFCDigital:menu') === 'open' ||
      !localStorage.getItem('@BFCDigital:menu')
    ) {
      setOpen(true)
    } else {
      setOpen(false)
    }
  }, [])

  // * A escolha guardada no servidor é a que vale entre máquinas; quando ela chega, corrige o
  // * cache local. `null` é quem nunca escolheu — aí o padrão de hoje (aberto) permanece.
  const appliedStoredCollapsed = useRef(false)

  useEffect(() => {
    if (collapsed === null || appliedStoredCollapsed.current) {
      return
    }

    appliedStoredCollapsed.current = true
    localStorage.setItem('@BFCDigital:menu', collapsed ? 'close' : 'open')
    setOpen(!collapsed)
  }, [collapsed])

  function handleMenu(open) {
    open
      ? localStorage.setItem('@BFCDigital:menu', 'close')
      : localStorage.setItem('@BFCDigital:menu', 'open')
    // Recolhido o campo de busca some da tela: deixar o termo valendo esconderia itens sem o
    // usuário ter como perceber por quê.
    if (open) {
      clearSearch()
    }
    setOpen(!open)
    setCollapsed(open)
  }

  // * Atalho Ctrl+B para abrir/fechar o menu.
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'b') {
        event.preventDefault()
        handleMenu(open)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  const wrapperRef = useRef(null)
  useOutsideAlerter(wrapperRef)

  const leaves = useMemo(() => collectMenuLeaves(config), [config])

  const leafByRoute = useMemo(() => {
    const map: Record<string, IMenuLeaf> = {}

    leaves.forEach(leaf => {
      map[leaf.route] = leaf
    })

    return map
  }, [leaves])

  const activeLeaf = useMemo(
    () => findActiveLeaf(leaves, router.asPath),
    [leaves, router.asPath]
  )

  const filteredConfig = useMemo(
    () => filterMenu(config, searchTerm),
    [config, searchTerm]
  )

  // * Filtragem com debounce: o campo responde a cada tecla, a lista só é remontada quando o
  // * usuário para de digitar.
  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearchTerm(searchInput.trim())
    }, SEARCH_DEBOUNCE)

    return () => clearTimeout(timeout)
  }, [searchInput])

  // * Enquanto há busca todo grupo com resultado aparece aberto. Ao limpar, o menu volta ao
  // * estado de expansão que tinha antes — por isso ele é guardado na entrada da busca.
  const groupOpenBeforeSearch = useRef<string | null>(null)
  const previousSearchTerm = useRef('')

  useEffect(() => {
    const previous = previousSearchTerm.current
    previousSearchTerm.current = searchTerm

    if (!previous && searchTerm) {
      groupOpenBeforeSearch.current = groupOpen
      return
    }

    if (previous && !searchTerm) {
      setGroupOpen(groupOpenBeforeSearch.current)
    }
  }, [searchTerm, groupOpen])

  // * O grupo da tela aberta expande sozinho, senão o destaque do subitem ativo ficaria escondido.
  // * Só reage à troca de grupo: dentro do mesmo grupo o usuário continua livre para fechá-lo.
  const lastActiveGroup = useRef<string | null>(null)

  useEffect(() => {
    const groupRoute = activeLeaf?.groupRoute || null

    if (groupRoute && groupRoute !== lastActiveGroup.current) {
      lastActiveGroup.current = groupRoute
      setGroupOpen(groupRoute)
    }
  }, [activeLeaf?.groupRoute])

  // * Toda tela do menu que o usuário abre entra em RECENTES, tenha ele chegado pelo menu, por um
  // * link interno ou digitando a URL.
  useEffect(() => {
    if (activeLeaf?.route) {
      registerVisit(activeLeaf.route)
    }
  }, [activeLeaf?.route, registerVisit])

  function handleNavigate(route: string) {
    onChangeActive(route)
    router.push(route)
  }

  function handleTogglePin(route: string) {
    togglePin(route)
  }

  function clearSearch() {
    setSearchInput('')
    setSearchTerm('')
  }

  /**
   * Move o foco entre os itens visíveis. É o foco real que anda, não um índice paralelo — assim o
   * Enter é o do próprio link e o item em foco fica visível para quem navega por teclado.
   */
  function focusItem(direction: 1 | -1, from?: HTMLElement) {
    const items = Array.from(
      scrollRef.current?.querySelectorAll<HTMLAnchorElement>(
        'a[data-menu-item]'
      ) || []
    )

    if (!items.length) {
      return
    }

    const currentIndex = from ? items.indexOf(from as HTMLAnchorElement) : -1

    const nextIndex =
      currentIndex === -1
        ? direction === 1
          ? 0
          : items.length - 1
        : (currentIndex + direction + items.length) % items.length

    items[nextIndex]?.focus()
  }

  function handleListKeyDown(event: React.KeyboardEvent) {
    const target = event.target as HTMLElement

    if (event.key === 'Escape' && searchInput) {
      event.preventDefault()
      clearSearch()
      searchInputRef.current?.focus()
      return
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return
    }

    if (!target.matches('a[data-menu-item]')) {
      return
    }

    event.preventDefault()
    focusItem(event.key === 'ArrowDown' ? 1 : -1, target)
  }

  const pinnedItems = useMemo(
    () => pinned.map(route => leafByRoute[route]).filter(Boolean),
    [pinned, leafByRoute]
  )

  const recentItems = useMemo(
    () =>
      recent
        .map(route => leafByRoute[route])
        .filter(Boolean)
        .filter(leaf => !pinned.includes(leaf.route)),
    [recent, pinned, leafByRoute]
  )

  // * A busca do rail usa o mesmo filtro do menu aberto, achatado em linhas. Sem termo não há
  // * resultado: despejar o menu inteiro no cartão não ajuda ninguém a encontrar nada.
  const searchResults = useMemo(
    () => (searchTerm ? collectMenuLeaves(filteredConfig) : []),
    [filteredConfig, searchTerm]
  )

  // Busca e atalhos ocupam largura: com o menu recolhido só a lista de ícones faz sentido.
  const showShortcuts = open && !searchTerm
  const hasShortcuts = pinnedItems.length > 0 || recentItems.length > 0
  const isSearching = Boolean(searchTerm)

  const head =
    active === null ? (
      <Head>
        <title>BFC Digital</title>
      </Head>
    ) : (
      <Head>
        <title>BFC Digital - {active?.title}</title>
      </Head>
    )

  // * Recolhido o menu tem outra anatomia — zonas de ícones e um cartão flutuante —, então ele
  // * monta uma árvore própria em vez de esconder pedaços da lista completa por CSS.
  if (!open) {
    return (
      <Container open={open} ref={wrapperRef} className="no-print menu-close">
        {head}
        <MenuRail
          config={config}
          pinnedItems={pinnedItems}
          recentItems={recentItems}
          activeRoute={activeLeaf?.route || null}
          searchTerm={searchInput}
          onSearchChange={setSearchInput}
          searchResults={searchResults}
          onNavigate={handleNavigate}
          onReorderPinned={reorderPinned}
          onToggleMenu={() => handleMenu(open)}
        />
      </Container>
    )
  }

  return (
    <Container open={open} ref={wrapperRef} className="no-print menu-open">
      <button
        type="button"
        className="burger"
        onClick={() => handleMenu(open)}
        title="Recolher menu"
        aria-label="Recolher menu"
        aria-expanded={open}
      >
        <strong className="desc">NAVEGAÇÃO</strong>
        <MdMenuOpen />
      </button>

      <div className="menu_search">
        <MdSearch className="menu_search__icon" />
        <input
          ref={searchInputRef}
          type="text"
          value={searchInput}
          placeholder="Buscar no menu"
          aria-label="Buscar no menu"
          onChange={e => setSearchInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'ArrowDown') {
              e.preventDefault()
              focusItem(1)
              return
            }

            if (e.key === 'ArrowUp') {
              e.preventDefault()
              focusItem(-1)
              return
            }

            if (e.key === 'Escape') {
              e.preventDefault()
              clearSearch()
            }
          }}
        />
        {searchInput ? (
          <button
            type="button"
            className="menu_search__clear"
            title="Limpar busca"
            aria-label="Limpar busca"
            onClick={() => {
              clearSearch()
              searchInputRef.current?.focus()
            }}
          >
            <MdClose />
          </button>
        ) : null}
      </div>

      <ScrollMenu
        className="scroll_menu"
        ref={scrollRef}
        onKeyDown={handleListKeyDown}
      >
        {head}

        {showShortcuts && hasShortcuts ? (
          <div className="menu_shortcuts">
            {pinnedItems.length ? (
              <>
                <span className="menu_shortcuts__label">FIXADOS</span>
                {pinnedItems.map((leaf, index) => (
                  <MenuItem
                    key={`pinned-${leaf.route}`}
                    route={leaf.route}
                    title={leaf.title}
                    isActive={leaf.route === activeLeaf?.route}
                    isPinned
                    onNavigate={handleNavigate}
                    onTogglePin={handleTogglePin}
                    className="is-pinned-item"
                    orderIndex={index}
                    orderCount={pinnedItems.length}
                    onReorder={reorderPinned}
                  />
                ))}
              </>
            ) : null}

            {recentItems.length ? (
              <>
                {/* Recolhível porque nem todo mundo se serve dela — a escolha fica salva. */}
                <button
                  type="button"
                  className="menu_shortcuts__toggle"
                  aria-expanded={!recentCollapsed}
                  title={
                    recentCollapsed
                      ? 'Mostrar telas recentes'
                      : 'Ocultar telas recentes'
                  }
                  onClick={() => setRecentCollapsed(!recentCollapsed)}
                >
                  <span className="menu_shortcuts__label">RECENTES</span>
                  {recentCollapsed ? <MdExpandMore /> : <MdExpandLess />}
                </button>
                {recentCollapsed
                  ? null
                  : recentItems.map(leaf => (
                      <MenuItem
                        key={`recent-${leaf.route}`}
                        route={leaf.route}
                        title={leaf.title}
                        isActive={leaf.route === activeLeaf?.route}
                        isPinned={false}
                        onNavigate={handleNavigate}
                        onTogglePin={handleTogglePin}
                        className="is-recent-item"
                        icon={<MdHistory className="menu_item__icon" />}
                      />
                    ))}
              </>
            ) : null}

            <div className="menu_shortcuts__divider" />
          </div>
        ) : null}

        {filteredConfig?.map((route, index) => {
          if (route.isDropdown) {
            return (
              <Dropdown
                key={route.route || index}
                route={route}
                open={isSearching || groupOpen === route.route}
                setOpen={() => {
                  setGroupOpen(groupOpen === route.route ? null : route.route)
                }}
                // O destaque acompanha o grupo que o usuário deixou aberto. Durante a busca a
                // expansão é resultado do filtro, e não de uma escolha dele, então ninguém fica
                // destacado.
                isActive={!isSearching && groupOpen === route.route}
                activeRoute={activeLeaf?.route || null}
                searchTerm={searchTerm}
                isPinned={isPinned}
                onNavigate={handleNavigate}
                onTogglePin={handleTogglePin}
              />
            )
          }
          return (
            <MenuItem
              key={route.route || index}
              route={route.route}
              title={route.title}
              isActive={route.route === activeLeaf?.route}
              isPinned={isPinned(route.route)}
              searchTerm={searchTerm}
              onNavigate={handleNavigate}
              onTogglePin={handleTogglePin}
              className="menu_item--root"
              icon={route.icon as React.ReactNode}
            />
          )
        })}

        {isSearching && !filteredConfig?.length ? (
          <p className="menu_empty">Nenhum item encontrado</p>
        ) : null}
      </ScrollMenu>
    </Container>
  )
}

export default Menu
